import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { TranslateService } from '@ngx-translate/core';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';
import { RequestLink, RequestLinkType } from 'src/app/request-v2/models/request.model';

export interface ValidatedRequestData {
    isPublicRequest: boolean;
    shouldDispatch: boolean;
    uploadedFileUrls?: string[];
}

@Component({
    selector: 'app-validate-information',
    templateUrl: './validate-information.component.html',
    styleUrls: ['./validate-information.component.scss'],
})
export class ValidateInformationComponent implements OnInit, OnDestroy {
    @Input() selectedCategory: any;

    @Input() selectedSubcategory: any;

    @Input() location: any;

    @Input() requestDescription: string;

    @Input() attachedFiles: any[] = [];

    @Input() requestLinks: RequestLink[] = [];

    @Input() isPublicRequest = false;

    @Output() requestValidated = new EventEmitter<ValidatedRequestData>();

    @Output() publicStatusChanged = new EventEmitter<boolean>();

    private destroy$ = new Subject<void>();

    uploadingFiles = false;

    errorMessage = '';

    constructor(
        private sanitizer: DomSanitizer,
        private requestsService: RequestsV2ImplService,
        private messageService: MessageService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        // Initialization logic if needed
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    deleteFile(index: number): void {
        if (this.attachedFiles && this.attachedFiles.length > index) {
            this.attachedFiles.splice(index, 1);
        }
    }

    onPublicStatusChange(event: any): void {
        this.isPublicRequest = event.target.checked;
        this.publicStatusChanged.emit(this.isPublicRequest);
    }

    getFileImageSrc(file: any): SafeUrl {
        if (file.file && file.file instanceof File) {
            // If we have the actual File object
            return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file.file));
        }
        if (file.preview) {
            // If we have a preview URL
            return this.sanitizer.bypassSecurityTrustUrl(file.preview);
        }
        if (file.url) {
            // If we have a URL
            return this.sanitizer.bypassSecurityTrustUrl(file.url);
        }
        if (file instanceof File) {
            // If the file itself is a File object
            return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
        }

        return '';
    }

    handleImageError(event: any): void {
        const imgElement = event.target;
        // Add a class for styling broken images
        imgElement.classList.add('broken-image');
        // Set a fallback image
        imgElement.src = '';
    }

    determineFileType(file: any): string {
        // Get either the file object or the file itself
        const fileObj = file.file instanceof File ? file.file : file;
        const filename = fileObj.name ? fileObj.name.toLowerCase() : '';
        const mimeType = fileObj.type ? fileObj.type.toLowerCase() : '';

        if (mimeType.startsWith('image/') || (file.type && file.type.startsWith('image/'))) {
            return 'image';
        }

        // Check Excel first by extension, which is more reliable
        if (filename.endsWith('.xls') || filename.endsWith('.xlsx') || filename.endsWith('.csv') || filename.endsWith('.xlsm')) {
            return 'excel';
        }

        // Then check Excel by MIME type
        if (
            mimeType.includes('spreadsheet') ||
            mimeType.includes('excel') ||
            mimeType.includes('sheet') ||
            mimeType === 'application/vnd.ms-excel' ||
            mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
            return 'excel';
        }

        // Check PDF
        if (filename.endsWith('.pdf') || mimeType.includes('pdf')) {
            return 'pdf';
        }

        // Check Word documents last to avoid misidentification
        if (
            filename.endsWith('.doc') ||
            filename.endsWith('.docx') ||
            mimeType.includes('word') ||
            mimeType.includes('document') ||
            mimeType === 'application/msword' ||
            mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) {
            return 'word';
        }

        return 'other';
    }

    isImageFile(file: any): boolean {
        return this.determineFileType(file) === 'image';
    }

    isPdfFile(file: any): boolean {
        return this.determineFileType(file) === 'pdf';
    }

    isWordFile(file: any): boolean {
        return this.determineFileType(file) === 'word';
    }

    isExcelFile(file: any): boolean {
        return this.determineFileType(file) === 'excel';
    }

    getLabelForType(type: RequestLinkType): string {
        return `request_v2.links.types.${type}`;
    }

    getLinkedRequestDisplayId(link: RequestLink): string {
        return link?.linkedRequest?.requestId || link?.linkedRequestId || '';
    }

    getRequestCategoryLabel(link: RequestLink): string {
        const linkedRequest = link?.linkedRequest;
        return linkedRequest?.subCategory?.name || linkedRequest?.category?.name || '';
    }

    validateAndProceed(): void {
        this.uploadingFiles = true;
        this.errorMessage = '';

        // Filter the files from attachedFiles that are actual file objects
        const filesToUpload = this.attachedFiles.filter((file) => file.file && file.file instanceof File).map((fileObj) => fileObj.file);

        if (filesToUpload.length > 0) {
            // Create an array of upload observables
            const uploadObservables: Observable<string[]>[] = filesToUpload.map((file) =>
                this.requestsService.uploadFile(file).pipe(
                    catchError((error) => {
                        console.error('Error uploading file:', error);
                        this.messageService.error(this.translate.instant('create-request-dialog.validate-information.file-upload-error'));
                        return of([]);
                    }),
                ),
            );

            // Execute all uploads in parallel
            forkJoin(uploadObservables)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => {
                        this.uploadingFiles = false;
                    }),
                )
                .subscribe({
                    next: (results: string[][]) => {
                        // Flatten the array of arrays and update attachedFiles with URLs
                        const uploadedFileUrls = results.reduce((acc, val) => acc.concat(val), []);

                        // Update the attachedFiles with the URLs
                        this.updateAttachedFilesWithUrls(uploadedFileUrls);

                        // Emit event to parent component with the uploaded file URLs
                        this.requestValidated.emit({
                            isPublicRequest: this.isPublicRequest,
                            shouldDispatch: false,
                            uploadedFileUrls,
                        });
                    },
                    error: (error) => {
                        console.error('Error during file upload:', error);
                        this.errorMessage = this.translate.instant('create-request-dialog.validate-information.file-upload-error');
                        this.uploadingFiles = false;
                    },
                });
        } else {
            // No files to upload, just validate
            this.uploadingFiles = false;
            this.requestValidated.emit({
                isPublicRequest: this.isPublicRequest,
                shouldDispatch: false,
            });
        }
    }

    private updateAttachedFilesWithUrls(fileUrls: string[]): void {
        // Find files that need URLs
        const filesNeedingUrls = this.attachedFiles.filter((file) => file.file && file.file instanceof File);

        // Assign URLs to files (assuming the order matches)
        filesNeedingUrls.forEach((file, index) => {
            if (index < fileUrls.length) {
                file.url = fileUrls[index];
            }
        });
    }
}
